import React, { memo, useMemo } from "react";
import {
  ComposedChart,
  Bar,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ReferenceLine,
  ResponsiveContainer,
  Cell,
} from "recharts";
import { formatLargeNumber } from "../../utils/formatUtils";
import { buildChartSeries, formatUnitsExact, pointLabel } from "../../utils/usageUtils";

// Bootstrap semantic hexes, matching AzureCostCharts.jsx and TrendsTab.jsx.
const COLOR_ACTUAL = "#0d6efd";
const COLOR_PARTIAL = "#9ec5fe";
const COLOR_FORECAST = "#6610f2";
const COLOR_LIMIT = "#dc3545";

/**
 * Tooltip that spells out what kind of point is being hovered. The distinction
 * matters: the current month is a partial total and the forecast is not observed data,
 * so neither should read as a completed month.
 */
function UsageTooltip({ active, payload }) {
  if (!active || !payload?.length) return null;

  const point = payload[0].payload;
  const value = point.isForecast ? point.forecastUnits : point.actualUnits;

  return (
    <div className="bg-body border rounded shadow-sm p-2 small">
      <div className="fw-semibold">{pointLabel(point)}</div>
      <div>{formatUnitsExact(value)} units</div>
      {point.percentOfLimit !== null && point.percentOfLimit !== undefined && (
        <div className="text-muted">{Number(point.percentOfLimit).toFixed(1)}% of limit</div>
      )}
      {point.isPartial && <div className="text-warning">Month still in progress</div>}
      {point.isForecast && <div className="text-muted fst-italic">Projected</div>}
    </div>
  );
}

/**
 * Monthly usage with the tier limit as a reference line and a dashed forward forecast.
 *
 * The in-progress month is drawn in a lighter fill so it is never mistaken for a real
 * drop — without that, every client appears to fall off a cliff in the current month.
 */
function UsageTrendChart({
  series = [],
  forecast = [],
  effectiveLimit,
  projectedCurrent = null,
  height = 350,
}) {
  const data = useMemo(
    () => buildChartSeries(series, forecast, projectedCurrent),
    [series, forecast, projectedCurrent]
  );

  if (!data.length) {
    return <div className="text-muted text-center py-5">No usage recorded for this period.</div>;
  }

  const showLimit = effectiveLimit > 0;

  return (
    <ResponsiveContainer width="100%" height={height}>
      <ComposedChart data={data} margin={{ top: 10, right: 20, left: 10, bottom: 5 }}>
        <CartesianGrid strokeDasharray="3 3" opacity={0.3} />
        <XAxis dataKey="name" tick={{ fontSize: 12 }} />
        <YAxis tickFormatter={(value) => formatLargeNumber(value)} tick={{ fontSize: 12 }} />
        <Tooltip content={<UsageTooltip />} />
        <Legend />

        {showLimit && (
          <ReferenceLine
            y={effectiveLimit}
            stroke={COLOR_LIMIT}
            strokeDasharray="6 4"
            label={{ value: "Tier limit", position: "insideTopRight", fontSize: 11, fill: COLOR_LIMIT }}
          />
        )}

        <Bar dataKey="actualUnits" name="Usage units" fill={COLOR_ACTUAL} radius={[2, 2, 0, 0]}>
          {data.map((point, index) => (
            <Cell key={index} fill={point.isPartial ? COLOR_PARTIAL : COLOR_ACTUAL} />
          ))}
        </Bar>

        {forecast.length > 0 && (
          <Line
            type="monotone"
            dataKey="forecastUnits"
            name="Forecast"
            stroke={COLOR_FORECAST}
            strokeDasharray="6 4"
            strokeWidth={2}
            dot={{ r: 3 }}
            connectNulls
          />
        )}
      </ComposedChart>
    </ResponsiveContainer>
  );
}

export default memo(UsageTrendChart);
